/**
 * The single-sourced fixed-step loop body (Tier 1, Phase A).
 *
 * main.ts (the game) and editor/Playtest.ts both used to inline an IDENTICAL step
 * body — snapshot → meta actions → world.update → endStep. Two copies that drift =
 * a ghost recorded under one and replayed under the other desyncs. So the body lives
 * HERE once; both owners call it. Live play uses {@link Input}; ghost playback uses a
 * {@link ReplaySource}. The Recorder taps the live source inside liveStep.
 *
 * Determinism: the sim only ever sees the resolved snapshot passed to world.update.
 * The meta action (reset) is live-input-only and excluded from any record.
 */
import type { InputSnapshot } from '../engine/Input';
import type { Input } from '../engine/Input';
import type { World } from '../engine/World';
import type { Recorder } from './Recorder';

/** Anything that yields one resolved snapshot per step — a ReplaySource (the ghost). */
type SnapshotSource = { snapshot(): InputSnapshot };

export interface LiveStepHooks {
  /** Taps each step's resolved live snapshot + the post-step clear flag (PB capture). */
  recorder?: Recorder;
  /** Fires once on the RISING edge of world.cleared (publish gate / PB save). */
  onClear?: () => void;
}

/**
 * One fixed step driven by LIVE input. Used by both the game (main.ts) and the
 * editor playtest. Handles the meta action (reset) that lives OUTSIDE the
 * deterministic sim, taps the recorder, and fires onClear on the clear edge.
 */
export function liveStep(world: World, input: Input, dt: number, hooks?: LiveStepHooks): void {
  const snap = input.snapshot();
  hooks?.recorder?.capture(snap); // tap the live source BEFORE meta handling
  const wasCleared = world.cleared;
  if (input.pressed('reset')) {
    world.respawn();
  } else {
    world.update(dt, snap);
  }
  hooks?.recorder?.afterStep(world.cleared);
  if (!wasCleared && world.cleared) hooks?.onClear?.();
  input.endStep(); // clear per-step edges after snapshot + meta are read
}

/**
 * One fixed step driven by a recorded {@link ReplaySource} (a ghost). No meta actions,
 * no Input, no recorder — just feed the next recorded snapshot into the SAME
 * world.update the live step uses, so the ghost can never drift from the original run.
 */
export function replayStep(world: World, source: SnapshotSource, dt: number): void {
  world.update(dt, source.snapshot());
}
